// Mão de obra do mês (06/10/2026) — lógica pura da tela (sem antd, testável no jest).
//
// A regra (Lucas, 06/10/2026): a remuneração paga a cada pessoa no mês é
// dividida pelas horas que ELA anotou. Hora em contrato → custo do contrato;
// hora em centro interno (Administrativo, Comercial…) → despesa, repartida
// entre os contratos pela receita; o que não foi anotado fica PARADO
// (pendente) até ser corrigido. Os números vêm prontos do servidor
// (GET /financeiro/fechamento-cc/mao-obra) — aqui só se soma, filtra e escreve.

import { formatarHoras } from '../operacional-horas/horasUtils';

export type ContratoDaPessoa = { contrato_id: string; numero: string | null; titulo: string | null; valor: number; horas: number; fontes: string[] };
export type PessoaMaoObra = {
  profissional_id: string | null; nome: string;
  pago_mes: number; h_total: number; h_contrato: number; h_interno: number; h_sem_destino: number;
  h_clockify: number; h_anotador: number; rs_hora: number;
  valor_contratos: number; valor_interno: number; valor_pendente: number; motivo_pendente: string | null;
  contratos?: ContratoDaPessoa[];
};
export type RespostaMaoObra = {
  competencia: string; corte: string; congelado: boolean; regra_antiga: boolean; em_andamento: boolean;
  execucao: { id: string; calculado_em: string } | null; pessoas: PessoaMaoObra[];
};

/** Por que o valor está parado — nome curto, cor e o que fazer. */
export const MOTIVO_PENDENTE: Record<string, { rotulo: string; cor: string; ajuda: string }> = {
  sem_horas: { rotulo: 'não anotou horas', cor: 'red',
    ajuda: 'Recebeu e não anotou nenhuma hora no mês. O valor fica parado até a pessoa anotar (Operacional › Horas).' },
  horas_sem_destino: { rotulo: 'horas sem contrato', cor: 'orange',
    ajuda: 'Parte das horas não chega a contrato nenhum: projeto do Clockify não ligado a contrato (ou entrada sem projeto), contrato apagado, ou centro de custo de projeto sem contrato. Corrija a hora ou ligue o projeto e recalcule o mês.' },
  mes_em_andamento: { rotulo: 'mês em andamento', cor: 'blue',
    ajuda: 'O mês ainda não fechou: a divisão pelas horas acontece quando ele acabar.' },
  sem_pessoa: { rotulo: 'sem a pessoa', cor: 'red',
    ajuda: 'Remuneração lançada sem a "Pessoa da equipe": não há de quem ler as horas. Ponha a pessoa no lançamento (Financeiro › Contas a Pagar).' },
};

const num = (v: unknown) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const horasTexto = (h: number) => formatarHoras(Math.round(num(h) * 60));
const r2 = (v: number) => Math.round(v * 100) / 100;

/** Totais do mês (cada número aparece uma vez: aqui o total, na tabela a pessoa). */
export function totaisDoMes(pessoas: readonly PessoaMaoObra[]) {
  return pessoas.reduce((t, p) => ({
    pago: r2(t.pago + num(p.pago_mes)),
    contratos: r2(t.contratos + num(p.valor_contratos)),
    interno: r2(t.interno + num(p.valor_interno)),
    pendente: r2(t.pendente + num(p.valor_pendente)),
    horas: r2(t.horas + num(p.h_total)),
  }), { pago: 0, contratos: 0, interno: 0, pendente: 0, horas: 0 });
}

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
export function filtrarPessoas(pessoas: readonly PessoaMaoObra[], texto: string): PessoaMaoObra[] {
  const q = semAcento(texto.trim());
  return q ? pessoas.filter(p => semAcento(p.nome || '').includes(q)) : [...pessoas];
}

/** Horas (decimal) → minutos, para o componente de total de horas ("7h30"). */
export const minutosDe = (h: number) => Math.round(num(h) * 60);

export const reais = (v: number) => `R$ ${num(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A frase que explica a conta de uma pessoa. */
export function contaDaPessoa(p: PessoaMaoObra): string {
  if (!p.profissional_id) return `${reais(p.pago_mes)} lançados sem a pessoa — parados até alguém pôr a pessoa no lançamento.`;
  if (num(p.pago_mes) === 0) return `Anotou ${horasTexto(p.h_total)} e não há remuneração com o nome dela nas linhas da equipe neste mês — confira se o pagamento está com a pessoa e no mês certo.`;
  if (p.motivo_pendente === 'mes_em_andamento') return `${reais(p.pago_mes)} pagos no mês, que ainda não fechou — a divisão acontece quando ele acabar.`;
  if (num(p.h_total) <= 0) return `${reais(p.pago_mes)} pagos e nenhuma hora anotada — o valor está parado até a pessoa anotar.`;
  return `${reais(p.pago_mes)} pagos ÷ ${horasTexto(p.h_total)} anotadas = ${reais(p.rs_hora)} por hora.`;
}

// ── As horas da pessoa, entrada por entrada (15/10/2026) ─────────────────
// GET /financeiro/fechamento-cc/mao-obra/horas — a MESMA classificação do motor.
export type DestinoHora = 'contrato' | 'interno' | 'sem_destino' | 'nao_conta';
export type EntradaHora = {
  id: string; fonte: 'clockify' | 'anotador'; dia: string; hora: string | null; horas: number;
  onde: string | null; descricao: string | null; destino: DestinoHora;
  contrato_id: string | null; contrato: string | null; contrato_titulo: string | null; centro: string | null;
  motivo: string | null; motivo_texto: string | null;
};
export type RespostaHorasPessoa = {
  competencia: string; profissional_id: string; nome: string; entradas: EntradaHora[];
  resumo: { total: number; contrato: number; interno: number; sem_destino: number; nao_conta: number };
  /** quantos usuários do Clockify a ficha tem no mês (0 = as horas do Clockify não chegam a esta pessoa) */
  usuarios_clockify?: number;
};
export const DESTINO_HORA: Record<DestinoHora, { rotulo: string; cor: string }> = {
  contrato: { rotulo: 'Contrato', cor: 'green' },
  interno: { rotulo: 'Centro interno', cor: 'blue' },
  sem_destino: { rotulo: 'Sem contrato', cor: 'orange' },
  nao_conta: { rotulo: 'Não conta', cor: 'default' },
};
/** O complemento da etiqueta: o contrato, o centro, ou o porquê. */
export function paraOndeFoi(e: EntradaHora): string {
  if (e.destino === 'contrato') return `${e.contrato || 'contrato'}${e.contrato_titulo ? ` — ${e.contrato_titulo}` : ''}`;
  if (e.destino === 'interno') return e.centro || 'centro interno';
  return e.motivo_texto || '';
}
export type ModoHoras = 'sem' | 'todas';
export function entradasVisiveis(entradas: readonly EntradaHora[], modo: ModoHoras): EntradaHora[] {
  return modo === 'sem' ? entradas.filter(e => e.destino === 'sem_destino') : [...entradas];
}
/** As horas de agora diferem das do cálculo do mês? (alguém corrigiu depois) */
export function horasMudaram(resumo: RespostaHorasPessoa['resumo'] | null | undefined, p: PessoaMaoObra): boolean {
  if (!resumo) return false;
  return Math.abs(num(resumo.total) - num(p.h_total)) > 0.01 || Math.abs(num(resumo.sem_destino) - num(p.h_sem_destino)) > 0.01;
}
/** Competência que veio no endereço (?competencia=2026/09), ou null. */
export const competenciaDaUrl = (v: string | null | undefined): string | null => (v && /^\d{4}\/\d{2}$/.test(v) ? v : null);
/** "2026-09-23" + "08:30" → "23/09 08:30". */
export const diaCurto = (dia: string, hora?: string | null) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}${hora ? ` ${hora}` : ''}`;

/** Mês anterior ao de hoje, no formato do servidor (o mês em andamento ainda não divide). */
export function mesAnterior(hoje: Date = new Date()): string {
  const d = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}
